import React, { useState, useEffect } from "react";
import {
  Select,
  Spin,
  Alert,
  Modal,
  Button,
  Typography,
  Upload,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { PDFDocument } from "pdf-lib";
import { addPatientDocuments } from "../../redux/doctorSlice";
import {
  TEMPLATE_CONFIG,
  CLINIC_CONSTANTS,
  GENDER_CHECKBOX_NAMES,
} from "../../../constants/requisitionCoords";
import Header from "./Components/Header";

const { Text, Title } = Typography;
const { Dragger } = Upload;

const REQUISITION_TYPES = [
  { label: "Day 3",             value: "Day3",             forGender: "female" },
  { label: "Day 21",            value: "Day21",            forGender: "female" },
  { label: "Peak +7 & Fasting", value: "PeakPlus7Fasting", forGender: "female" },
  { label: "Male",              value: "Male",             forGender: "male"   },
  { label: "Semen Analysis",    value: "Semen",            forGender: "male"   },
  { label: "Blank",             value: "Blank",            forGender: "any"    },
  { label: "Referral Letter",   value: "Referral",         forGender: "any"    },
  { label: "Custom",            value: "Custom",           forGender: "any"    },
];

const getRequisitionOptions = (patient) => {
  const gender = (patient?.gender || patient?.sex || "").toLowerCase();
  const isMale = gender.startsWith("m");
  const isFemale = gender.startsWith("f");
  return REQUISITION_TYPES.filter((t) => {
    if (t.forGender === "any") return true;
    if (isMale)   return t.forGender === "male";
    if (isFemale) return t.forGender === "female";
    return true; // unknown gender — show all
  });
};

const formatDob = (dob) => {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d.getTime())) return dob;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${String(d.getDate()).padStart(2, "0")}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const buildFilledPdf = async (requisitionType, patient, doctor) => {
  const config = TEMPLATE_CONFIG[requisitionType];
  if (!config) throw new Error(`Unknown requisition type: ${requisitionType}`);

  const res = await fetch(`/templates/${encodeURIComponent(config.filename)}`);
  if (!res.ok) throw new Error(`Template not found: ${config.filename}`);

  const arrayBuffer = await res.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
  const form = pdfDoc.getForm();

  const setField = (fieldName, value) => {
    try {
      form.getTextField(fieldName).setText(value || "");
    } catch {
      // field not present in this template - skip it
    }
  };

  const checkBox = (fieldName) => {
    try { form.getCheckBox(fieldName).check(); } catch { /* not in this template */ }
  };

  const C = CLINIC_CONSTANTS;
  const doctorId = doctor?.licenseNumber || doctor?.licenceNumber || "";

  // Logical value bags — a template's field map references these keys.
  const patientValues = {
    uli:        patient.uli || "",
    dob:        formatDob(patient.dob),
    lastName:   patient.lastname || "",
    firstName:  patient.firstname || "",
    fullName:   `${patient.firstname || ""} ${patient.lastname || ""}`.trim(),
    phone:      patient.phoneNumber || "",
    address:    patient.address || "",
    city:       patient.city || "",
    province:   patient.stateOrProvince || "",
    postalCode: patient.postalCode || "",
  };

  const doctorLastFirst = doctor
    ? `${doctor.lastName || ""}, ${doctor.firstName || ""}`.trim().replace(/^,\s*/, "")
    : "";
  const doctorFirstLast = doctor
    ? `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim()
    : "";

  const providerValues = {
    providerName:       doctorLastFirst,
    referringClinician: doctorFirstLast,
    providerAddress:    C.clinicAddress,
    officeAddress:      C.clinicAddress,
    providerID:         doctorId,
    providerLicense:    doctorId,
    submitterID:        C.submitterID,
    providerEmail:      doctor?.email || "",
    providerPhone:      C.phone,
    providerFax:        C.fax,
    clinicName:         C.clinicName,
    date:               formatDob(new Date()),
  };

  
  Object.entries(config.patientFields).forEach(([key, fieldName]) =>
    setField(fieldName, patientValues[key])
  );
  Object.entries(config.providerFields).forEach(([key, fieldName]) =>
    setField(fieldName, providerValues[key])
  );

  // Gender checkboxes
  if (config.hasGender) {
    const G = GENDER_CHECKBOX_NAMES;
    const gender = (patient.gender || patient.sex || "").toLowerCase();
    if (gender.includes("non") || gender.includes("binary"))            checkBox(G.nonBinary);
    else if (gender.includes("prefer") || gender.includes("disclose"))  checkBox(G.preferNot);
    else if (gender.startsWith("m"))                                    checkBox(G.male);
    else if (gender.startsWith("f"))                                    checkBox(G.female);
  }

  // Fields remain editable so the clinician can review and adjust in the browser viewer
  const bytes = await pdfDoc.save();
  const base64 = btoa(
    new Uint8Array(bytes).reduce((data, byte) => data + String.fromCharCode(byte), "")
  );
  const last  = (patient.lastname  || "").replace(/\s+/g, "");
  const first = (patient.firstname || "").replace(/\s+/g, "");
  return { base64, filename: `${requisitionType}_${last}${first}.pdf` };
};

const NewLabRequisition = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const patient    = JSON.parse(localStorage.getItem("patient") || "{}");
  const doctor     = useSelector((s) => s.authentication?.userAuth?.obj);
  const bloodWorkFile2 = useSelector((s) => s.doctor.bloodWorkFile2);

  const [selectedType, setSelectedType] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [customFile, setCustomFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [localPdfB64, setLocalPdfB64] = useState(null);
  const [localFilename, setLocalFilename] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  const atCapacity = (bloodWorkFile2 || []).length >= 5;

  useEffect(() => {
    if (!patient.userRef) {
      navigate("/doctor/labs");
    }
  }, [patient.userRef, navigate]);

  const handleTypeChange = async (value) => {
    setSelectedType(value);
    setCustomFile(null);
    setUploaded(false);
    setLocalPdfB64(null);
    setLocalFilename(null);
    setLocalError(null);

    if (value !== "Custom") {
      setLocalLoading(true);
      try {
        const result = await buildFilledPdf(value, patient, doctor);
        setLocalPdfB64(result.base64);
        setLocalFilename(result.filename);
      } catch (err) {
        setLocalError(err.message);
      } finally {
        setLocalLoading(false);
      }
    }
  };

  const isCustom = selectedType === "Custom";
  const previewReady = isCustom ? !!customFile : !!localPdfB64;

  const handleUploadConfirm = async () => {
    setConfirmOpen(false);
    setUploading(true);

    let base64, filename, fileTitle;

    if (isCustom) {
      const reader = new FileReader();
      reader.readAsDataURL(customFile);
      await new Promise((resolve) => {
        reader.onload = () => {
          base64 = reader.result.split(",")[1];
          filename = customFile.name;
          fileTitle = "Custom";
          resolve();
        };
      });
    } else {
      const pdfDoc = await PDFDocument.load(
        Uint8Array.from(atob(localPdfB64), (c) => c.charCodeAt(0))
      );
      pdfDoc.getForm().flatten();
      const flatBytes = await pdfDoc.save();
      base64 = btoa(
        new Uint8Array(flatBytes).reduce((d, b) => d + String.fromCharCode(b), "")
      );
      filename = localFilename;
      fileTitle = REQUISITION_TYPES.find((t) => t.value === selectedType)?.label ?? selectedType;
    }

    const resultAction = await dispatch(
      addPatientDocuments({
        patientRef: patient.userRef,
        bloodWork: [{ base64, filename, fileTitle, fileType: 2 }],
      })
    );

    setUploading(false);

    if (addPatientDocuments.fulfilled.match(resultAction)) {
      setUploaded(true);
    } else {
      message.error("Upload failed. Please try again.");
    }
  };

  const selectedLabel = REQUISITION_TYPES.find((t) => t.value === selectedType)?.label ?? selectedType;
  const patientName = `${patient.firstname ?? ""} ${patient.lastname ?? ""}`.trim();

  return (
    <React.Fragment>
      {patient?.userRef ? <Header /> : null}

      <div style={{ padding: "24px" }}>
        <Button
          type="link"
          onClick={() => navigate("/doctor/labs")}
          style={{ paddingLeft: 0, marginBottom: "16px", color: "#00ADEF" }}
        >
          ← Back To Labs and Requisitions
        </Button>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <Title level={4} style={{ margin: 0 }}>NEW LAB REQUISITION DETAILS</Title>
          {uploaded && (
            <Alert
              message="Lab Requisition Uploaded"
              type="success"
              showIcon
              closable
              style={{ marginLeft: "16px" }}
            />
          )}
        </div>

        {atCapacity && (
          <Alert
            message="Maximum reached: you can only upload 5 requisitions per day."
            type="warning"
            showIcon
            style={{ marginBottom: "16px" }}
          />
        )}

        <div style={{ marginBottom: "24px" }}>
          <Text strong style={{ display: "block", marginBottom: "8px" }}>
            Select Requisition Type
          </Text>
          <Select
            style={{ width: "100%", maxWidth: "320px" }}
            placeholder="Select a type..."
            options={getRequisitionOptions(patient)}
            onChange={handleTypeChange}
            value={selectedType}
          />
        </div>

        {selectedType && !isCustom && (
          <div
            style={{
              border: "1px solid #C2E6F8",
              borderRadius: "8px",
              minHeight: "600px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "24px",
              background: "#fafafa",
            }}
          >
            {localLoading ? (
              <Spin size="large" tip="Generating requisition..." />
            ) : localPdfB64 ? (
              <iframe
                title="Requisition Preview"
                src={`data:application/pdf;base64,${localPdfB64}`}
                style={{ width: "100%", height: "600px", border: "none", borderRadius: "8px" }}
              />
            ) : localError ? (
              <div style={{ textAlign: "center", padding: "32px" }}>
                <Text type="danger" style={{ display: "block", marginBottom: "8px" }}>
                  Could not load template: {localError}
                </Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Make sure the template PDF exists in <code>/public/templates/</code>.
                </Text>
              </div>
            ) : (
              <Text type="secondary">Loading preview...</Text>
            )}
          </div>
        )}

        {isCustom && (
          <div style={{ marginBottom: "24px" }}>
            <Text strong style={{ display: "block", marginBottom: "8px" }}>Upload Requisition File</Text>
            <Dragger
              name="file"
              multiple={false}
              showUploadList={false}
              beforeUpload={(file) => {
                const allowed = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
                if (!allowed.includes(file.type)) {
                  message.error("Only PDF, JPG, and PNG files are allowed.");
                  return Upload.LIST_IGNORE;
                }
                setCustomFile(file);
                return false;
              }}
              style={{ padding: "24px" }}
            >
              <p className="ant-upload-drag-icon"><UploadOutlined /></p>
              <p className="ant-upload-text">Drag and drop</p>
              <p className="ant-upload-hint">- OR -</p>
              <Button icon={<UploadOutlined />}>Browse Files</Button>
            </Dragger>
            {customFile && (
              <Text style={{ display: "block", marginTop: "8px" }}>Selected: {customFile.name}</Text>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: "12px" }}>
          <Button onClick={() => navigate("/doctor/labs")}>Cancel</Button>
          <Button
            type="primary"
            style={{ background: "#00ADEF" }}
            disabled={!previewReady || atCapacity || uploading}
            loading={uploading}
            onClick={() => setConfirmOpen(true)}
          >
            Upload Requisition
          </Button>
        </div>

        <Modal
          open={confirmOpen}
          onCancel={() => setConfirmOpen(false)}
          title={null}
          centered
          footer={null}
          closable={false}
          width={400}
        >
          <div style={{ textAlign: "center", marginBottom: "24px", padding: "0 8px" }}>
            <Text style={{ color: "#00ADEF", fontSize: "15px", fontWeight: 500 }}>
              Are you sure you want to upload the following requisition?
            </Text>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "28px", gap: "0" }}>
            <div style={{ display: "flex", width: "280px", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #f0f0f0" }}>
              <Text type="secondary">Patient</Text>
              <Text strong>{patientName || "—"}</Text>
            </div>
            <div style={{ display: "flex", width: "280px", justifyContent: "space-between", padding: "10px 0" }}>
              <Text type="secondary">Requisition Type</Text>
              <Text strong>{selectedLabel}</Text>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              style={{ minWidth: "90px", borderColor: "#00ADEF", color: "#00ADEF" }}
            >
              No
            </Button>
            <Button
              type="primary"
              style={{ background: "#00ADEF", minWidth: "90px", border: "none" }}
              onClick={handleUploadConfirm}
            >
              Yes
            </Button>
          </div>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default NewLabRequisition;
